/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { cn } from "~/lib/utils";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const pluggingInDevice = imageUrl("/images/landing/plugging-in-device.jpg");

/**
 * Ported from bouncieweb's `LandingPageComponents/EasyInstallation.js`.
 *
 * Faithful reproduction of the original Emotion-styled section. The original
 * wraps `GridItemDarkBlueGradient` but overrides the background to solid black,
 * so the surviving Bento styling is just: rounded corners (20px), overflow
 * hidden, relative positioning, white text, and the bottom grid-gap margin.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  sm 640px -> sm:  md 768px -> md:
 *   lg 1024px -> lg:  xlg 1280px -> xl:
 */

// Original: styled.div — inline-block, no-wrap, small horizontal padding so each
// phrase ("No tools." / "No wires." / "It just plugs in.") stays on one line.
export function EasyInstallationSubText({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <span className="inline-block px-1 whitespace-nowrap">{children}</span>
  );
}

function compatibilityClick() {
  const compatibility = document.getElementById("obd-compatibility-scroll-target");
  if (compatibility) {
    compatibility.scrollIntoView({ behavior: "smooth" });
  }
}

export default function EasyInstallation({
  preHeading,
  heading,
  comboBottom = false,
}: {
  preHeading: React.ReactNode;
  heading: React.ReactNode;
  /**
   * Square off the bottom corners and drop the bottom margin so this section
   * fuses into whatever sits directly below it (pages pair it with
   * `<Compatibility comboTop />`).
   */
  comboBottom?: boolean;
}) {
  return (
    <section
      className={cn(
        // GridItemDarkBlueGradient residue: rounded, clipped, relative, white text.
        "relative flex flex-col items-start overflow-hidden rounded-[20px] text-white",
        // Original forces solid black over the dark-blue gradient at every size.
        "bg-black p-0 md:items-end",
        comboBottom ? "rounded-b-none" : "mb-6",
      )}
    >
      {/* EasyInstallationContent */}
      <div
        className={cn(
          "z-[1] flex w-full flex-col justify-center p-6 text-center",
          "md:w-[19rem] md:pt-16 md:pb-12 md:text-left",
          "lg:w-[28rem] xl:w-[30rem]",
        )}
      >
        {/* CustomLargeP */}
        <p
          className={cn(
            "mx-auto mb-2 w-full text-[18px]",
            "min-[431px]:mx-0 min-[431px]:max-w-none min-[431px]:text-[22px]",
            "md:text-[20px] lg:text-[24px]",
          )}
        >
          {preHeading}
        </p>
        {/* CustomH2 */}
        <h2
          className={cn(
            "font-display relative mx-auto mb-2 max-w-[16rem] font-extrabold",
            "text-[7vw] leading-[1.2]",
            "min-[431px]:mx-0 min-[431px]:max-w-none min-[431px]:text-[31px] min-[431px]:leading-[1.3]",
            "md:text-[30px] lg:text-[40px]",
          )}
        >
          {heading}
        </h2>
        {/* CustomP */}
        <p
          className={cn(
            "min-[431px]:mx-auto min-[431px]:max-w-[23rem] min-[431px]:[text-wrap:wrap]",
            "md:mx-0",
          )}
        >
          Works in 1996 and newer passenger vehicles. Some EVs may not be{" "}
          <button data-sid="easy-installation-compatibility-link"
            type="button"
            className="gtm-trigger-scroll-to-compatibility cursor-pointer underline"
            onClick={compatibilityClick}
          >
            compatible
          </button>
          .
        </p>
      </div>

      {/* PluggingInDeviceImage */}
      <Picture
        src={pluggingInDevice}
        alt="A hand plugging the Bouncie device into a vehicle's OBD2 port"
        // Large and near the fold: it is the LCP element on the Connecting Family page at desktop width.
        loading="eager"
        className={cn(
          // Mobile: oversized, pulled up/left, faded in from the top.
          "mt-[-5.5rem] ml-[-20%] h-fit w-[250%] max-w-none",
          "[mask-image:linear-gradient(180deg,transparent,black_20%)]",
          // md+: absolutely-positioned cover image faded in from the left.
          "md:absolute md:m-0 md:h-full md:w-full md:object-cover md:object-[27%_center]",
          "md:[mask-image:linear-gradient(90deg,black_30%,rgba(0,0,0,0.5)_70%)]",
          "lg:object-center",
        )}
      />
    </section>
  );
}
